import * as Y from "yjs"

// A plain <input> or <textarea> kept in step with a Y.Text, and other people's cursors drawn over it. Whatever the
// field's own code does to its value (typing, paste, a toolbar splicing Markdown in, which fires `input`) is turned
// into the smallest edit on the Y.Text; whatever other people do to the Y.Text is put into the field with your own
// cursor and selection kept where they were in the text.
//
//   const binding = new TextBinding(ytext, field, { awareness, name: "body" })
//   binding.destroy()
//
// Each person's selection in this field goes into the shared awareness (as Yjs relative positions, so it survives
// edits above it) under `cursors.<name>`, and everyone else's is drawn as a coloured caret with their name.
const LOCAL = "text-binding"

const MIRRORED = ["boxSizing", "width", "height", "overflowX", "overflowY", "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
    "paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "fontStyle", "fontVariant", "fontWeight", "fontStretch", "fontSize", "lineHeight",
    "fontFamily", "textAlign", "textTransform", "textIndent", "letterSpacing", "wordSpacing", "tabSize"]

export class TextBinding {
    constructor(ytext, field, { awareness, name }) {
        this.ytext = ytext
        this.field = field
        this.awareness = awareness
        this.name = name
        this.shadow = ytext.toString()
        this.field.value = this.shadow

        this._onInput = () => this._fromField()
        this._onSelect = () => this._announceSelection()
        this._onScroll = () => this._drawCarets()
        this._onRemote = (event, transaction) => { if (transaction.origin !== LOCAL) this._fromText() }
        this._onAwareness = () => this._drawCarets()
        field.addEventListener("input", this._onInput)
        ;["keyup", "click", "focus", "select"].forEach((type) => field.addEventListener(type, this._onSelect))
        field.addEventListener("scroll", this._onScroll)
        ytext.observe(this._onRemote)
        awareness.on("change", this._onAwareness)

        this.layer = document.createElement("div")
        this.layer.className = "collab-carets"
        this.layer.setAttribute("aria-hidden", "true")
        field.parentElement.style.position ||= "relative"
        field.after(this.layer)
        this._drawCarets()
    }

    destroy() {
        this.field.removeEventListener("input", this._onInput)
        ;["keyup", "click", "focus", "select"].forEach((type) => this.field.removeEventListener(type, this._onSelect))
        this.field.removeEventListener("scroll", this._onScroll)
        this.ytext.unobserve(this._onRemote)
        this.awareness.off("change", this._onAwareness)
        this.layer.remove()
    }

    // What the field holds now, as an edit on the shared text: everything between the common start and the common end
    // of the old and new value is replaced.
    _fromField() {
        const next = this.field.value
        const prev = this.shadow
        if (next === prev) return

        let start = 0
        const limit = Math.min(prev.length, next.length)
        while (start < limit && prev[start] === next[start]) start += 1
        let end = 0
        while (end < limit - start && prev[prev.length - 1 - end] === next[next.length - 1 - end]) end += 1
        this.shadow = next
        this.ytext.doc.transact(() => {
            if (prev.length - start - end > 0) this.ytext.delete(start, prev.length - start - end)
            if (next.length - start - end > 0) this.ytext.insert(start, next.slice(start, next.length - end))
        }, LOCAL)
        this._announceSelection()
    }

    // Someone else's edit: the field takes the new text, and your cursor stays at the same place in the words.
    _fromText() {
        const doc = this.ytext.doc
        const hadFocus = document.activeElement === this.field
        const anchor = Y.createRelativePositionFromTypeIndex(this.ytext, this.field.selectionStart)
        const head = Y.createRelativePositionFromTypeIndex(this.ytext, this.field.selectionEnd)
        const scroll = this.field.scrollTop
        this.shadow = this.ytext.toString()
        this.field.value = this.shadow
        const from = Y.createAbsolutePositionFromRelativePosition(anchor, doc)
        const to = Y.createAbsolutePositionFromRelativePosition(head, doc)
        if (hadFocus && from && to) this.field.setSelectionRange(from.index, to.index)
        this.field.scrollTop = scroll
        this._drawCarets()
    }

    _announceSelection() {
        const cursors = { ...(this.awareness.getLocalState()?.cursors || {}) }
        if (document.activeElement !== this.field) {
            delete cursors[this.name]
        } else {
            cursors[this.name] = {
                anchor: Y.relativePositionToJSON(Y.createRelativePositionFromTypeIndex(this.ytext, this.field.selectionStart)),
                head: Y.relativePositionToJSON(Y.createRelativePositionFromTypeIndex(this.ytext, this.field.selectionEnd))
            }
        }
        this.awareness.setLocalStateField("cursors", cursors)
    }

    _drawCarets() {
        const doc = this.ytext.doc
        this.layer.replaceChildren()
        this.awareness.getStates().forEach((state, clientId) => {
            const cursor = state.cursors?.[this.name]
            if (clientId === doc.clientID || !cursor || !state.user) return

            const head = Y.createAbsolutePositionFromRelativePosition(Y.createRelativePositionFromJSON(cursor.head), doc)
            if (!head || head.type !== this.ytext) return

            const { top, left, height } = this._coordinates(head.index)
            const caret = document.createElement("span")
            caret.className = "collab-caret"
            caret.style.cssText = `top:${top}px;left:${left}px;height:${height}px;border-color:${state.user.color};`
            const label = document.createElement("span")
            label.className = "collab-caret-label"
            label.style.backgroundColor = state.user.color
            label.textContent = state.user.name
            caret.append(label)
            this.layer.append(caret)
        })
    }

    // Where the text before `index` ends inside the field: the field's text laid out in a hidden copy of it with the
    // same box and font, measured at a marker. Relative to the field's parent, scroll taken off.
    _coordinates(index) {
        const field = this.field
        const style = getComputedStyle(field)
        const mirror = document.createElement("div")
        mirror.style.cssText = "position:absolute;visibility:hidden;white-space:pre-wrap;overflow-wrap:break-word;top:0;left:-9999px;"
        MIRRORED.forEach((property) => { mirror.style[property] = style[property] })
        if (field.tagName === "INPUT") mirror.style.whiteSpace = "pre"
        mirror.textContent = field.value.slice(0, index)
        const marker = document.createElement("span")
        marker.textContent = field.value.slice(index) || "."
        mirror.append(marker)
        document.body.append(mirror)
        const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2
        const result = {
            top: field.offsetTop + marker.offsetTop + parseFloat(style.borderTopWidth) - field.scrollTop,
            left: field.offsetLeft + marker.offsetLeft + parseFloat(style.borderLeftWidth) - field.scrollLeft,
            height: lineHeight
        }
        mirror.remove()
        return result
    }
}
